import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useDeletePerDiemPolicy,
  usePerDiemPoliciesQuery,
} from "../api/queries";
import { PerDiemPolicyTable } from "../ui/PerDiemPolicyTable";

export function PerDiemPoliciesPage() {
  const nav = useNavigate({ from: "/app/performance/per-diem" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/performance/per-diem",
  });
  const list = usePerDiemPoliciesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canCreate = useActionPermission(["performance-per-diem:create"]);
  const deletePolicy = useDeletePerDiemPolicy();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ເບ້ຍລ້ຽງຕ່າງແຂວງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ນະໂຍບາຍເບ້ຍລ້ຽງຕ່າງແຂວງ
            </h2>
            <p className="text-muted-foreground">
              ກຳນົດອັດຕາກິນຢູ່/ພັກຕາມນະໂຍບາຍບໍລິສັດ — finance ປັບຈຳນວນເງິນໄດ້
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => nav({ to: "/app/performance/per-diem/create" })}
            >
              <PlusIcon className="size-4" /> ເພີ່ມນະໂຍບາຍ
            </Button>
          )}
        </div>

        <div className="rounded-xl border bg-card pt-2">
          <PerDiemPolicyTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(item) =>
              nav({
                to: "/app/performance/per-diem/$id/edit",
                params: { id: item.id },
              })
            }
            onDelete={async (id) => {
              try {
                await deletePolicy.mutateAsync(id);
                toast.success("ລຶບນະໂຍບາຍແລ້ວ");
              } catch {}
            }}
          />
        </div>
      </Main>
    </>
  );
}
